import { fetchWithAuth } from '../AuthService';
import { DATA_SERVICE_BASE_URL } from '../ApiService';

/**
 * Enrollment API service — manages course enrollments for shared courses
 */
export const EnrollmentApi = {
  /**
   * Get current user's enrollments
   * @returns {Promise<Response>} List of enrollment DTOs
   */
  getMyEnrollments: async () => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/users/enrollments`, {
      method: 'GET',
    });
  },

  /**
   * Get enrolled users for an endeavor (paginated)
   * @param {string|number} endeavorId
   * @param {Object} options
   * @param {number} [options.page=0]
   * @param {number} [options.size=50]
   * @returns {Promise<Response>} Paginated enrollment list
   */
  getEnrolledUsers: async (endeavorId, options = {}) => {
    const { page = 0, size = 50 } = options;
    const queryParams = new URLSearchParams({ page: String(page), size: String(size) }).toString();
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/enrollments?${queryParams}`, {
      method: 'GET',
    });
  },

  /**
   * Get all enrolled users for an endeavor (no pagination)
   * @param {string|number} endeavorId
   * @returns {Promise<Response>} All enrollments
   */
  getAllEnrollments: async (endeavorId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/enrollments/all`, {
      method: 'GET',
    });
  },

  /**
   * Invite a student by email
   * @param {string|number} endeavorId
   * @param {string} email
   * @returns {Promise<Response>} Enrollment DTO
   */
  inviteStudent: async (endeavorId, email) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/invite`, {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ email }),
    });
  },

  /**
   * Unenroll a student from an endeavor
   * @param {string|number} endeavorId
   * @param {string|number} userId
   * @returns {Promise<Response>}
   */
  unenrollStudent: async (endeavorId, userId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/enrollments/${userId}`, {
      method: 'DELETE',
    });
  },

  /**
   * Get enrollment count for an endeavor
   * @param {string|number} endeavorId
   * @returns {Promise<Response>} { count: number }
   */
  getEnrollmentCount: async (endeavorId) => {
    return fetchWithAuth(`${DATA_SERVICE_BASE_URL}/api/endeavors/${endeavorId}/enrollments/count`, {
      method: 'GET',
    });
  },
};
